const ALLOWED_ELEMENTS = new Set([
  "svg",
  "g",
  "path",
  "circle",
  "ellipse",
  "rect",
  "line",
  "polyline",
  "polygon",
  "defs",
  "clippath",
  "mask",
  "title",
  "desc",
  "lineargradient",
  "radialgradient",
  "stop",
  "text",
  "tspan",
  "marker",
  "symbol",
  "pattern",
  "filter",
  "fegaussianblur",
  "feoffset",
  "feblend",
  "fecolormatrix",
  "fecomposite",
  "femerge",
  "femergenode",
  "feflood",
]);

const GRAPHIC_ELEMENTS = new Set([
  "path",
  "circle",
  "ellipse",
  "rect",
  "line",
  "polyline",
  "polygon",
  "text",
  "tspan",
]);

const ALLOWED_ATTRIBUTES = new Set([
  "viewbox",
  "width",
  "height",
  "xmlns",
  "fill",
  "stroke",
  "stroke-width",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-miterlimit",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-opacity",
  "fill-opacity",
  "fill-rule",
  "clip-rule",
  "d",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "x",
  "y",
  "x1",
  "y1",
  "x2",
  "y2",
  "dx",
  "dy",
  "points",
  "transform",
  "opacity",
  "gradientunits",
  "gradienttransform",
  "offset",
  "stop-color",
  "stop-opacity",
  "spreadmethod",
  "fx",
  "fy",
  "id",
  "class",
  "clip-path",
  "mask",
  "filter",
  "font-size",
  "font-family",
  "font-weight",
  "text-anchor",
  "dominant-baseline",
  "preserveaspectratio",
  "overflow",
  "role",
  "aria-hidden",
  "aria-label",
  "focusable",
  "version",
  "xml:space",
  "stddeviation",
  "in",
  "in2",
  "result",
  "mode",
  "type",
  "values",
  "operator",
  "k1",
  "k2",
  "k3",
  "k4",
  "flood-color",
  "flood-opacity",
  "patternunits",
  "patterntransform",
  "patterncontentunits",
  "markerwidth",
  "markerheight",
  "refx",
  "refy",
  "orient",
  "markerunits",
]);

const DANGEROUS_VALUE = /(?:javascript|vbscript|data)\s*:/i;

const isSafeFragmentUrl = (value: string): boolean => {
  if (!value.toLowerCase().startsWith("url(") || !value.endsWith(")")) {
    return false;
  }
  const inner = value.slice(4, -1).trim();
  if (!inner.startsWith("#") || inner.length < 2) {
    return false;
  }
  for (let i = 1; i < inner.length; i += 1) {
    const char = inner[i];
    if (char.startsWith(")") || char.trim() === "") {
      return false;
    }
  }
  return true;
};

const isDangerousValue = (value: string): boolean => {
  const trimmed = value.trim();
  if (DANGEROUS_VALUE.test(trimmed)) return true;
  if (trimmed.toLowerCase().includes("url(") && !isSafeFragmentUrl(trimmed)) {
    return true;
  }
  return false;
};

const localName = (element: Element): string =>
  (element.localName || element.nodeName).replace(/^.*:/, "").toLowerCase();

const hasGraphicContent = (element: Element): boolean => {
  if (GRAPHIC_ELEMENTS.has(localName(element))) return true;
  return Array.from(element.children).some(child => hasGraphicContent(child));
};

const sanitizeElement = (element: Element): void => {
  for (const attr of Array.from(element.attributes)) {
    const name = attr.name.toLowerCase();
    if (
      name.startsWith("on") ||
      name.includes("href") ||
      name === "src" ||
      name === "xlink:src" ||
      name === "style" ||
      !ALLOWED_ATTRIBUTES.has(name)
    ) {
      element.removeAttribute(attr.name);
      continue;
    }
    if (isDangerousValue(attr.value)) {
      element.removeAttribute(attr.name);
    }
  }

  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const childElement = child as Element;
      if (!ALLOWED_ELEMENTS.has(localName(childElement))) {
        element.removeChild(child);
        continue;
      }
      sanitizeElement(childElement);
    } else if (
      child.nodeType === Node.COMMENT_NODE ||
      child.nodeType === Node.CDATA_SECTION_NODE ||
      child.nodeType === Node.PROCESSING_INSTRUCTION_NODE
    ) {
      element.removeChild(child);
    }
  }
};

export const sanitizeSvgMarkup = (raw: string): string => {
  const trimmed = raw.replace(/^\uFEFF/, "").trim();
  if (!trimmed || !/<svg[\s>/]/i.test(trimmed)) {
    throw new Error("Invalid SVG");
  }
  if (/<!DOCTYPE/i.test(trimmed) || /<!ENTITY/i.test(trimmed)) {
    throw new Error("Invalid SVG");
  }

  const doc = new DOMParser().parseFromString(trimmed, "image/svg+xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("Invalid SVG");
  }

  const root = doc.documentElement;
  if (!root || localName(root) !== "svg") {
    throw new Error("Invalid SVG");
  }

  sanitizeElement(root);

  if (!hasGraphicContent(root)) {
    throw new Error("Invalid SVG");
  }

  const clean = new XMLSerializer().serializeToString(doc).trim();
  if (
    !/<svg[\s>/]/i.test(clean) ||
    /\son[a-z]+\s*=/i.test(clean) ||
    /<script/i.test(clean) ||
    /javascript:/i.test(clean)
  ) {
    throw new Error("Invalid SVG");
  }

  return clean;
};

export const sanitizeSvgFile = async (file: File): Promise<File> => {
  const clean = sanitizeSvgMarkup(await file.text());
  return new File([clean], file.name, { type: "image/svg+xml" });
};
